import { useState } from "react";
import { Calendar, FileText, Download } from "lucide-react";
import { format, parse, startOfMonth, endOfMonth, endOfDay, isWithinInterval, parseISO, eachDayOfInterval, isSameMonth, isSameDay, addDays } from "date-fns";
import jsPDF from "jspdf";
import autoTable from 'jspdf-autotable';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useToast } from "@/hooks/use-toast";
import type { CalendarEvent } from "@shared/schema";

interface CalendarExportDialogProps {
  open: boolean;
  onClose: () => void;
  events: CalendarEvent[];
}

export default function CalendarExportDialog({ 
  open, 
  onClose, 
  events = []
}: CalendarExportDialogProps) {
  const [viewType, setViewType] = useState<"calendar" | "list">("list");
  const [orientation, setOrientation] = useState<"portrait" | "landscape">("portrait");
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [isGenerating, setIsGenerating] = useState(false);
  const { toast } = useToast();

  // Helper: Convert hex color to RGB for jsPDF
  const hexToRgb = (hex: string) => {
    // Remove # if present
    hex = hex.replace('#', '');
    
    // Handle shorthand hex (#fff -> #ffffff)
    if (hex.length === 3) {
      hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
    }
    
    // Validate hex length
    if (hex.length !== 6) {
      // Default to black for invalid colors
      return { r: 0, g: 0, b: 0 };
    }
    
    // Parse hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Check for NaN (invalid hex digits)
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
      return { r: 0, g: 0, b: 0 };
    }
    
    return { r, g, b };
  };

  // Helper: Build calendar grid for a month (6 weeks, 42 days)
  const buildMonthGrid = (monthDate: Date) => {
    const firstDay = startOfMonth(monthDate);
    const lastDay = endOfMonth(monthDate);
    
    // Start from the Sunday before the first day
    const startCalendar = new Date(firstDay);
    startCalendar.setDate(startCalendar.getDate() - firstDay.getDay());
    
    // Build 42-day grid (6 weeks)
    const days = [];
    for (let i = 0; i < 42; i++) {
      const date = addDays(startCalendar, i);
      days.push({
        date,
        isCurrentMonth: isSameMonth(date, monthDate),
      });
    }
    
    return days;
  };

  // Helper: Expand multi-day events to appear on each spanned day
  const expandMultiDayEvents = (events: CalendarEvent[]) => {
    const expandedEvents: Array<{ date: Date; event: CalendarEvent; isContinuation: boolean }> = [];
    
    events.forEach(event => {
      const start = new Date(event.startTime);
      const end = new Date(event.endTime);
      
      // Normalize to date-only (remove time component)
      const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
      const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
      
      // Get all days this event spans
      const days = eachDayOfInterval({ start: startDay, end: endDay });
      
      days.forEach((day, index) => {
        expandedEvents.push({
          date: day,
          event,
          isContinuation: index > 0,
        });
      });
    });
    
    return expandedEvents;
  };

  const generatePDF = async () => {
    try {
      setIsGenerating(true);

      // Parse dates as local dates (not UTC) to avoid timezone issues
      const start = parse(startDate, 'yyyy-MM-dd', new Date());
      const end = endOfDay(parse(endDate, 'yyyy-MM-dd', new Date()));
      
      // Events are already filtered by category, type, and visibility from parent
      // Only apply date range filter here
      const filteredEvents = events.filter(event => {
        const eventDate = new Date(event.startTime);
        return isWithinInterval(eventDate, { start, end });
      });

      if (filteredEvents.length === 0) {
        toast({
          title: "No Events Found",
          description: "No events found in the selected date range.",
          variant: "destructive",
        });
        setIsGenerating(false);
        return;
      }

      // Only show months within the selected date range
      const rangeMonths = eachDayOfInterval({ start, end })
        .map(day => format(day, "yyyy-MM"));
      const monthsSet = new Set(rangeMonths);
      
      // Sort months chronologically
      const sortedMonths = Array.from(monthsSet).sort();
      
      // Debug: Log the date range and months being exported
      console.log('PDF Export Date Range:', {
        startDate,
        endDate,
        parsedStart: start,
        parsedEnd: end,
        monthsToExport: sortedMonths,
        totalEvents: filteredEvents.length
      });
      
      // Helper: Get events that overlap with a given month
      const getEventsForMonth = (monthKey: string) => {
        // Parse as local date to avoid timezone issues
        const monthStart = startOfMonth(parse(monthKey + "-01", 'yyyy-MM-dd', new Date()));
        const monthEnd = endOfMonth(parse(monthKey + "-01", 'yyyy-MM-dd', new Date()));
        
        return filteredEvents.filter(event => {
          const eventStart = new Date(event.startTime);
          const eventEnd = new Date(event.endTime);
          
          // Check if event overlaps with this month
          return eventStart <= monthEnd && eventEnd >= monthStart;
        });
      };

      // Create PDF with selected orientation
      const doc = new jsPDF({ orientation: orientation });
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      // Adjust margin based on orientation - landscape has more horizontal space
      const margin = orientation === "landscape" ? 15 : 20;

      // Add title page
      doc.setFontSize(20);
      doc.text("Calendar Export", pageWidth / 2, 30, { align: "center" });
      doc.setFontSize(12);
      doc.text(`${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`, pageWidth / 2, 40, { align: "center" });
      doc.text(`View: ${viewType === "calendar" ? "Calendar View" : "List View"}`, pageWidth / 2, 50, { align: "center" });
      doc.text(`Total Events: ${filteredEvents.length}`, pageWidth / 2, 60, { align: "center" });

      if (viewType === "calendar") {
        // CALENDAR VIEW
        for (const monthKey of sortedMonths) {
          const monthEvents = getEventsForMonth(monthKey);
          // Parse as local date to avoid timezone issues
          const monthDate = parse(monthKey + "-01", 'yyyy-MM-dd', new Date());
          
          doc.addPage();
          
          // Month header
          doc.setFontSize(18);
          doc.setFont("helvetica", "bold");
          doc.text(format(monthDate, "MMMM yyyy"), pageWidth / 2, margin, { align: "center" });
          
          // Calendar grid setup
          const headerHeight = margin + 10;
          const gridWidth = pageWidth - (margin * 2);
          const gridHeight = pageHeight - headerHeight - margin - 10;
          const cellWidth = gridWidth / 7;
          const cellHeight = gridHeight / 6;
          const gridStartY = headerHeight + 5;
          
          // Day headers
          const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          
          dayNames.forEach((day, col) => {
            const x = margin + (col * cellWidth);
            doc.text(day, x + cellWidth / 2, gridStartY, { align: "center" });
          });
          
          // Draw grid
          doc.setDrawColor(200, 200, 200);
          doc.setLineWidth(0.1);
          
          // Vertical lines
          for (let col = 0; col <= 7; col++) {
            const x = margin + (col * cellWidth);
            doc.line(x, gridStartY + 5, x, gridStartY + 5 + (cellHeight * 6));
          }
          
          // Horizontal lines
          for (let row = 0; row <= 6; row++) {
            const y = gridStartY + 5 + (row * cellHeight);
            doc.line(margin, y, margin + gridWidth, y);
          }
          
          // Build calendar days
          const calendarDays = buildMonthGrid(monthDate);
          
          // Expand multi-day events
          const expandedEvents = expandMultiDayEvents(monthEvents);
          
          // Group expanded events by date
          const eventsByDate = new Map<string, Array<{ event: CalendarEvent; isContinuation: boolean }>>();
          expandedEvents.forEach(({ date, event, isContinuation }) => {
            const dateKey = format(date, "yyyy-MM-dd");
            if (!eventsByDate.has(dateKey)) {
              eventsByDate.set(dateKey, []);
            }
            eventsByDate.get(dateKey)!.push({ event, isContinuation });
          });
          
          // Render each day
          calendarDays.forEach((day, index) => {
            const row = Math.floor(index / 7);
            const col = index % 7;
            const x = margin + (col * cellWidth);
            const y = gridStartY + 5 + (row * cellHeight);
            
            // Day number
            doc.setFontSize(9);
            if (day.isCurrentMonth) {
              doc.setTextColor(0, 0, 0);
              doc.setFont("helvetica", "bold");
            } else {
              doc.setTextColor(150, 150, 150);
              doc.setFont("helvetica", "normal");
            }
            doc.text(format(day.date, "d"), x + 2, y + 5);
            
            // Events for this day
            const dateKey = format(day.date, "yyyy-MM-dd");
            const dayEvents = eventsByDate.get(dateKey) || [];
            
            if (dayEvents.length > 0) {
              // Sort by start time
              const sortedDayEvents = dayEvents.sort((a, b) => 
                new Date(a.event.startTime).getTime() - new Date(b.event.startTime).getTime()
              );
              
              // Show up to 3 events in portrait, 4 in landscape (more horizontal space)
              const maxEvents = orientation === "landscape" ? 4 : 3;
              const eventsToShow = sortedDayEvents.slice(0, maxEvents);
              
              doc.setFontSize(6);
              doc.setFont("helvetica", "normal");
              doc.setTextColor(0, 0, 0);
              
              eventsToShow.forEach((item, eventIndex) => {
                const { event, isContinuation } = item;
                const eventY = y + 8 + (eventIndex * 3);
                
                // Apply event color if available
                if (event.color) {
                  const rgb = hexToRgb(event.color);
                  doc.setTextColor(rgb.r, rgb.g, rgb.b);
                } else {
                  doc.setTextColor(0, 0, 0);
                }
                
                // Format event text
                let eventText = "";
                if (!event.allDay && !isContinuation) {
                  eventText = `${format(new Date(event.startTime), "HH:mm")} `;
                }
                eventText += event.title || "Untitled";
                if (isContinuation) {
                  eventText += " (cont.)";
                }
                
                // Truncate to fit in cell
                const maxWidth = cellWidth - 4;
                const truncated = doc.splitTextToSize(eventText, maxWidth);
                doc.text(truncated[0], x + 2, eventY);
              });
              
              // Show overflow indicator
              if (dayEvents.length > maxEvents) {
                const overflowY = y + 8 + (maxEvents * 3);
                doc.setFontSize(5);
                doc.setTextColor(100, 100, 100);
                doc.text(`+${dayEvents.length - maxEvents} more`, x + 2, overflowY);
              }
            }
          });
        }
      } else {
        // LIST VIEW - Table Format
        doc.addPage();
        
        // Sort all filtered events chronologically
        const sortedEvents = filteredEvents.sort((a, b) => 
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
        );
        
        // Prepare table data
        const tableData = sortedEvents.map(event => {
          const startTime = new Date(event.startTime);
          const endTime = new Date(event.endTime);
          
          // Format Start column
          let startStr = "";
          if (event.allDay) {
            startStr = format(startTime, "MMM d, yyyy");
          } else {
            startStr = `${format(startTime, "MMM d, yyyy")}\n${format(startTime, "h:mm a")}`;
          }
          
          // Format End column
          let endStr = "";
          if (event.allDay) {
            endStr = format(endTime, "MMM d, yyyy");
          } else {
            endStr = `${format(endTime, "MMM d, yyyy")}\n${format(endTime, "h:mm a")}`;
          }
          
          return [
            event.title || "Untitled",
            startStr,
            endStr,
            event.location || "-",
            event.description || "-",
            event.eventType || "event",
            event.category || "-"
          ];
        });
        
        // Create table with autoTable
        autoTable(doc, {
          head: [['Title', 'Start', 'End', 'Location', 'Description', 'Type', 'Category']],
          body: tableData,
          startY: 35,
          margin: { top: 35, bottom: 20, left: margin, right: margin },
          theme: 'grid',
          headStyles: {
            fillColor: [255, 255, 255],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0.1,
            lineColor: [0, 0, 0],
          },
          styles: {
            fontSize: 9,
            cellPadding: 2,
            overflow: 'linebreak',
            valign: 'top',
          },
          columnStyles: {
            0: { cellWidth: 'auto' }, // Title - flexible
            1: { cellWidth: 28 }, // Start
            2: { cellWidth: 28 }, // End
            3: { cellWidth: 25 }, // Location
            4: { cellWidth: 'auto' }, // Description
            5: { cellWidth: 20 }, // Type
            6: { cellWidth: 25 }, // Category
          },
          didDrawPage: (data) => {
            // Add header on every page
            doc.setFontSize(18);
            doc.setFont("helvetica", "bold");
            doc.text("Firm Calendar", pageWidth / 2, 15, { align: "center" });
            
            // Date range on second line
            doc.setFontSize(12);
            doc.setFont("helvetica", "normal");
            doc.text(`${format(start, "MMM d, yyyy")} - ${format(end, "MMM d, yyyy")}`, pageWidth / 2, 23, { align: "center" });
            
            // Add page number at bottom
            const pageNumber = (doc as any).internal.getCurrentPageInfo().pageNumber;
            doc.setFontSize(10);
            doc.setFont("helvetica", "normal");
            doc.text(
              `Page ${pageNumber}`,
              pageWidth / 2,
              pageHeight - 10,
              { align: "center" }
            );
          },
        });
      }

      // Save PDF
      const filename = `calendar-export-${format(start, "yyyy-MM-dd")}-to-${format(end, "yyyy-MM-dd")}.pdf`;
      doc.save(filename);

      toast({
        title: "PDF Generated",
        description: `Successfully exported ${filteredEvents.length} events to PDF.`,
      });

      onClose();
    } catch (error) {
      console.error("PDF generation error:", error);
      toast({
        title: "Export Failed",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onOpenChange={(isOpen) => {
        // Only close if explicitly requested and not generating
        if (!isOpen && !isGenerating) {
          onClose();
        }
      }}
    >
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="dialog-calendar-export">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export Calendar to PDF
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* View Type Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Export Format</Label>
            <RadioGroup
              value={viewType}
              onValueChange={(value) => setViewType(value as "calendar" | "list")}
              className="flex flex-col space-y-2"
            >
              <div className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent">
                <RadioGroupItem value="list" id="list-view" data-testid="radio-list-view" />
                <Label htmlFor="list-view" className="flex items-center gap-2 cursor-pointer flex-1">
                  <FileText className="h-4 w-4" />
                  <div>
                    <div className="font-medium">List View</div>
                    <div className="text-xs text-muted-foreground">
                      Events listed chronologically with full details
                    </div>
                  </div>
                </Label>
              </div>
              
              <div className="flex items-center space-x-3 rounded-lg border p-3 hover:bg-accent">
                <RadioGroupItem value="calendar" id="calendar-view" data-testid="radio-calendar-view" />
                <Label htmlFor="calendar-view" className="flex items-center gap-2 cursor-pointer flex-1">
                  <Calendar className="h-4 w-4" />
                  <div>
                    <div className="font-medium">Calendar View</div>
                    <div className="text-xs text-muted-foreground">
                      Visual month grid with dates and events
                    </div>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Page Orientation Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Page Orientation</Label>
            <RadioGroup
              value={orientation}
              onValueChange={(value) => setOrientation(value as "portrait" | "landscape")}
              className="flex gap-4"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="portrait" id="portrait-orientation" data-testid="radio-portrait" />
                <Label htmlFor="portrait-orientation" className="cursor-pointer">
                  Portrait
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="landscape" id="landscape-orientation" data-testid="radio-landscape" />
                <Label htmlFor="landscape-orientation" className="cursor-pointer">
                  Landscape
                </Label>
              </div>
            </RadioGroup>
            <p className="text-xs text-muted-foreground">
              {orientation === "portrait" 
                ? "Standard vertical layout (8.5\" × 11\")" 
                : "Wide horizontal layout (11\" × 8.5\") - More space for calendar cells"}
            </p>
          </div>

          {/* Date Range Selection */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Date Range</Label>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="start-date" className="text-xs text-muted-foreground">
                  Start Date
                </Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  data-testid="input-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="end-date" className="text-xs text-muted-foreground">
                  End Date
                </Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  data-testid="input-end-date"
                />
              </div>
            </div>
          </div>

          {/* Preview Info */}
          <div className="rounded-lg bg-muted p-3 text-sm">
            <p className="font-medium mb-1">Export Preview</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Each month will be on a separate page</li>
              <li>• Events include: Title, Date, Category, Location, Details</li>
              <li>• Events are sorted chronologically within each month</li>
            </ul>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isGenerating}
            data-testid="button-cancel-export"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={generatePDF}
            disabled={isGenerating || !startDate || !endDate}
            data-testid="button-generate-pdf"
          >
            {isGenerating ? (
              <>
                <Download className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Generate PDF
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
